package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.data.models.MembershipDataItem
import com.rainbowclubs.rainbow.data.models.MembershipDataTypes
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.theme.*
import org.jetbrains.compose.resources.decodeToImageBitmap
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.*

@Composable
fun ManageClubScreen(
    clubId: String,
    viewModel: ManageClubViewModel = koinViewModel(parameters = { parametersOf(clubId) }),
    navigateTo: (ManageClubViewModel.NavigationEvent) -> Unit,
) {
    val uiState by viewModel.uiState.collectAsState()
    val pickClubImage = rememberClubImagePicker(
        onImageSelected = viewModel::onImageSelected,
    )

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) {
        Box(
            modifier = Modifier
                .padding(it)
                .fillMaxSize(),
        ) {
            when {
                uiState.isLoading -> LoadingScreen(Modifier.fillMaxSize())
                uiState.formError != null && uiState.name.isBlank() -> {
                    val error = uiState.formError ?: Res.string.club_form_error
                    ErrorScreen(
                        error = stringResource(error),
                        onButtonClick = viewModel::fetchClub,
                        buttonText = stringResource(Res.string.try_again_button),
                    )
                }

                else -> ManageClubScreenContent(
                    uiState = uiState,
                    onBackClick = viewModel::onBackClick,
                    onNameChanged = viewModel::onNameChanged,
                    onDescriptionChanged = viewModel::onDescriptionChanged,
                    onUploadImageClick = pickClubImage,
                    onAddFieldClick = viewModel::onAddMembershipFieldClick,
                    onEditFieldClick = viewModel::onEditMembershipFieldClick,
                    onDeleteFieldClick = viewModel::onDeleteMembershipFieldClick,
                    onSaveClick = viewModel::saveClub,
                    onEditorLabelChanged = viewModel::onEditorLabelChanged,
                    onEditorRequiredChanged = viewModel::onEditorRequiredChanged,
                    onEditorTypeChanged = viewModel::onEditorTypeChanged,
                    onDismissEditor = viewModel::dismissMembershipEditor,
                    onSaveEditor = viewModel::saveMembershipEditor,
                )
            }
        }
    }
}

@Composable
private fun ManageClubScreenContent(
    uiState: ManageClubUiState,
    onBackClick: () -> Unit,
    onNameChanged: (String) -> Unit,
    onDescriptionChanged: (String) -> Unit,
    onUploadImageClick: () -> Unit,
    onAddFieldClick: () -> Unit,
    onEditFieldClick: (Int) -> Unit,
    onDeleteFieldClick: (Int) -> Unit,
    onSaveClick: () -> Unit,
    onEditorLabelChanged: (String) -> Unit,
    onEditorRequiredChanged: (Boolean) -> Unit,
    onEditorTypeChanged: (MembershipDataTypes) -> Unit,
    onDismissEditor: () -> Unit,
    onSaveEditor: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState()),
    ) {
        EventFormHeader(
            title = Res.string.manage_club,
            description = Res.string.manage_club_page_description,
            icon = Res.drawable.ic_document_edit,
            iconContentDescription = Res.string.edit_icon_cd,
            onBackClick = onBackClick,
        )

        Spacer(modifier = Modifier.height(40.dp))

        ClubImageCard(
            imageBytes = uiState.clubImageBytes,
            onUploadImageClick = onUploadImageClick,
        )

        Spacer(modifier = Modifier.height(34.dp))

        EventFormSection(
            title = Res.string.club_profile,
            iconContentDescription = Res.string.edit_icon_cd,
            accent = MaterialTheme.colorScheme.accentGreen,
        )

        Spacer(modifier = Modifier.height(24.dp))

        OutlinedTextField(
            value = uiState.name,
            onValueChange = onNameChanged,
            label = { Text(stringResource(Res.string.club_name_label)) },
            placeholder = { Text(stringResource(Res.string.club_name_placeholder)) },
            leadingIcon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_document_edit),
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                )
            },
            supportingText = uiState.nameError?.let { error ->
                { Text(stringResource(error)) }
            },
            isError = uiState.nameError != null,
            textStyle = MaterialTheme.typography.titleMedium,
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(16.dp))

        OutlinedTextField(
            value = uiState.description,
            onValueChange = onDescriptionChanged,
            label = { Text(stringResource(Res.string.club_description_label)) },
            placeholder = { Text(stringResource(Res.string.club_description_placeholder)) },
            leadingIcon = {
                Icon(
                    painter = painterResource(Res.drawable.ic_text_fields),
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                )
            },
            minLines = 3,
            textStyle = MaterialTheme.typography.titleMedium,
            modifier = Modifier.fillMaxWidth(),
        )

        Spacer(modifier = Modifier.height(34.dp))

        MembershipDataHeader(onAddFieldClick = onAddFieldClick)

        Spacer(modifier = Modifier.height(18.dp))

        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            uiState.membershipData.forEachIndexed { index, item ->
                MembershipDataCard(
                    item = item,
                    onEditClick = { onEditFieldClick(index) },
                    onDeleteClick = { onDeleteFieldClick(index) },
                )
            }
        }

        Spacer(modifier = Modifier.height(20.dp))

        FormErrorText(error = uiState.formError)

        Spacer(modifier = Modifier.height(26.dp))

        EventSubmitButton(
            text = Res.string.save_changes_button,
            isSubmitting = uiState.isSubmitting,
            onClick = onSaveClick,
        )

        Spacer(modifier = Modifier.height(36.dp))
    }

    uiState.editor?.let { editor ->
        MembershipDataEditorDialog(
            editor = editor,
            onLabelChanged = onEditorLabelChanged,
            onRequiredChanged = onEditorRequiredChanged,
            onTypeChanged = onEditorTypeChanged,
            onDismiss = onDismissEditor,
            onSave = onSaveEditor,
        )
    }
}

@Composable
private fun ClubImageCard(
    imageBytes: ByteArray?,
    onUploadImageClick: () -> Unit,
) {
    val imageBitmap = remember(imageBytes) {
        imageBytes?.let { bytes ->
            runCatching { bytes.decodeToImageBitmap() }.getOrNull()
        }
    }

    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .height(178.dp),
        shape = RoundedCornerShape(28.dp),
        color = MaterialTheme.colorScheme.accentGreen.copy(alpha = 0.06f),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.accentGreen.copy(alpha = 0.18f)),
    ) {
        Box(
            modifier = Modifier.padding(16.dp),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .clip(RoundedCornerShape(24.dp))
                    .background(MaterialTheme.colorScheme.rainbowGradient),
            ) {
                imageBitmap?.let { bitmap ->
                    Image(
                        bitmap = bitmap,
                        contentDescription = stringResource(Res.string.club_image_preview),
                        contentScale = ContentScale.Fit,
                        modifier = Modifier.fillMaxSize(),
                    )
                }

                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(
                            Brush.linearGradient(
                                colors = listOf(
                                    MaterialTheme.colorScheme.scrim.copy(alpha = 0.06f),
                                    MaterialTheme.colorScheme.scrim.copy(alpha = 0.18f),
                                )
                            )
                        ),
                )


                Button(
                    onClick = onUploadImageClick,
                    modifier = Modifier
                        .align(Alignment.BottomEnd)
                        .padding(16.dp)
                        .height(44.dp),
                    shape = RoundedCornerShape(22.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        contentColor = MaterialTheme.colorScheme.accentGreen,
                    ),
                ) {
                    Icon(
                        painter = painterResource(Res.drawable.ic_upload),
                        contentDescription = null,
                        modifier = Modifier.size(20.dp),
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = stringResource(Res.string.upload_image),
                        color = MaterialTheme.colorScheme.onSurface,
                        style = MaterialTheme.typography.titleLarge,
                    )
                }
            }
        }
    }
}

@Composable
private fun MembershipDataHeader(
    onAddFieldClick: () -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    painter = painterResource(Res.drawable.ic_dashboard_grid),
                    contentDescription = null,
                    modifier = Modifier.size(24.dp),
                    tint = MaterialTheme.colorScheme.accentBlue,
                )
                Spacer(modifier = Modifier.width(16.dp))
                Text(
                    text = stringResource(Res.string.club_membership_data),
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                )
            }

            Spacer(modifier = Modifier.height(10.dp))

            Text(
                text = stringResource(Res.string.club_membership_data_description),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
            )
        }

        Spacer(modifier = Modifier.width(12.dp))

        Button(
            onClick = onAddFieldClick,
            shape = RoundedCornerShape(22.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = MaterialTheme.colorScheme.accentBlue.copy(alpha = 0.10f),
                contentColor = MaterialTheme.colorScheme.accentBlue,
            ),
            contentPadding = ButtonDefaults.ButtonWithIconContentPadding,
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_add),
                contentDescription = null,
                modifier = Modifier.size(18.dp),
            )
            Spacer(modifier = Modifier.width(6.dp))
            Text(
                text = stringResource(Res.string.add_field),
                style = MaterialTheme.typography.titleMedium,
            )
        }
    }
}

@Composable
private fun MembershipDataCard(
    item: MembershipDataItem,
    onEditClick: () -> Unit,
    onDeleteClick: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        color = MaterialTheme.colorScheme.accentBlue.copy(alpha = 0.055f),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.accentBlue.copy(alpha = 0.16f)),
    ) {
        Row(
            modifier = Modifier.padding(14.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
//            Box(
//                modifier = Modifier
//                    .size(48.dp)
//                    .background(MaterialTheme.colorScheme.surface, RoundedCornerShape(18.dp)),
//                contentAlignment = Alignment.Center,
//            ) {
//                Icon(
//                    painter = painterResource(Res.drawable.ic_text_fields),
//                    contentDescription = null,
//                    modifier = Modifier.size(26.dp),
//                    tint = MaterialTheme.colorScheme.accentBlue,
//                )
//            }

            Spacer(modifier = Modifier.width(14.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = item.label,
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(modifier = Modifier.height(4.dp))

                Text(
                    text = membershipTypeLabel(item.type),
                    color = MaterialTheme.colorScheme.outline,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )

                Spacer(modifier = Modifier.height(16.dp))

                RequiredChip(required = item.required)
            }

            Spacer(modifier = Modifier.width(8.dp))



            IconButton(onClick = onEditClick) {
                Icon(
                    painter = painterResource(Res.drawable.ic_document_edit),
                    contentDescription = stringResource(Res.string.edit_icon_cd),
                    tint = MaterialTheme.colorScheme.accentPurple,
                )
            }

            IconButton(onClick = onDeleteClick) {
                Icon(
                    painter = painterResource(Res.drawable.ic_delete),
                    contentDescription = stringResource(Res.string.delete_icon_cd),
                    tint = MaterialTheme.colorScheme.error,
                )
            }
        }
    }
}

@Composable
private fun RequiredChip(required: Boolean) {
    val contentColor = if (required) {
        MaterialTheme.colorScheme.accentGreen
    } else {
        MaterialTheme.colorScheme.outline
    }
    Surface(
        shape = RoundedCornerShape(18.dp),
        color = MaterialTheme.colorScheme.surface,
    ) {
        Text(
            text = stringResource(if (required) Res.string.required_field else Res.string.optional_label),
            color = contentColor,
            style = MaterialTheme.typography.titleMedium,
            modifier = Modifier.padding(horizontal = 14.dp, vertical = 6.dp),
        )
    }
}

@Composable
private fun MembershipDataEditorDialog(
    editor: MembershipDataEditorUiState,
    onLabelChanged: (String) -> Unit,
    onRequiredChanged: (Boolean) -> Unit,
    onTypeChanged: (MembershipDataTypes) -> Unit,
    onDismiss: () -> Unit,
    onSave: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                text = stringResource(Res.string.edit_membership_field),
                style = MaterialTheme.typography.titleLarge,
            )
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                OutlinedTextField(
                    value = editor.label,
                    onValueChange = onLabelChanged,
                    label = { Text(stringResource(Res.string.club_field_label)) },
                    supportingText = editor.labelError?.let { error ->
                        { Text(stringResource(error)) }
                    },
                    isError = editor.labelError != null,
                    textStyle = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.fillMaxWidth(),
                )

                MembershipTypeDropdown(
                    selectedType = editor.type,
                    onTypeSelected = onTypeChanged,
                )

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = stringResource(Res.string.required_field),
                            color = MaterialTheme.colorScheme.onSurface,
                            style = MaterialTheme.typography.titleLarge,
                        )
                        Text(
                            text = stringResource(if (editor.required) Res.string.required_field else Res.string.optional_label),
                            color = MaterialTheme.colorScheme.outline,
                            style = MaterialTheme.typography.titleMedium,
                        )
                    }
                    Switch(
                        checked = editor.required,
                        onCheckedChange = onRequiredChanged,
                    )
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onSave) {
                Text(
                    text = stringResource(Res.string.save_field),
                    style = MaterialTheme.typography.titleLarge,
                )
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text(
                    text = stringResource(Res.string.cancel),
                    style = MaterialTheme.typography.titleLarge,
                )
            }
        },
    )
}

@Composable
private fun MembershipTypeDropdown(
    selectedType: MembershipDataTypes,
    onTypeSelected: (MembershipDataTypes) -> Unit,
) {
    var expanded by remember { mutableStateOf(false) }
    val types = MembershipDataTypes.entries.filter { it != MembershipDataTypes.UNKNOWN }

    Box {
        OutlinedButton(
            onClick = { expanded = true },
            modifier = Modifier.fillMaxWidth(),
            shape = RoundedCornerShape(14.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = stringResource(Res.string.club_field_type),
                        color = MaterialTheme.colorScheme.outline,
                        style = MaterialTheme.typography.titleMedium,
                    )
                    Text(
                        text = membershipTypeLabel(selectedType),
                        color = MaterialTheme.colorScheme.onSurface,
                        style = MaterialTheme.typography.titleLarge,
                    )
                }
                Text(
                    text = stringResource(Res.string.select_field_type),
                    color = MaterialTheme.colorScheme.primary,
                    style = MaterialTheme.typography.titleMedium,
                )
            }
        }

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.fillMaxWidth(),
        ) {
            types.forEach { type ->
                DropdownMenuItem(
                    text = {
                        Text(
                            text = membershipTypeLabel(type),
                            style = MaterialTheme.typography.titleMedium,
                        )
                    },
                    onClick = {
                        onTypeSelected(type)
                        expanded = false
                    },
                )
            }
        }
    }
}

@Composable
private fun membershipTypeLabel(type: MembershipDataTypes): String {
    return when (type) {
        MembershipDataTypes.SHORT_TEXT -> "Short text"
        MembershipDataTypes.LONG_TEXT -> "Long text"
        MembershipDataTypes.NUMBER -> "Number"
        MembershipDataTypes.DROP_DOWN -> "Dropdown"
        MembershipDataTypes.MULTI_SELECT -> "Multi select"
        MembershipDataTypes.DATE -> "Date"
        MembershipDataTypes.BOOLEAN -> "Boolean"
        MembershipDataTypes.UNKNOWN -> "Unknown"
    }
}

@Preview(name = "Membership Data Card")
@Composable
private fun MembershipDataCardPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                MembershipDataCard(
                    item = MembershipDataItem(
                        label = "Emergency contact",
                        type = MembershipDataTypes.NUMBER,
                        required = false,
                    ),
                    onEditClick = {},
                    onDeleteClick = {},
                )
            }
        }
    }
}

@Preview(name = "Manage Club Screen")
@Composable
private fun ManageClubScreenPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                ManageClubScreenContent(
                    uiState = ManageClubUiState(
                        isLoading = false,
                        name = "Rainbow Club",
                        description = "A safe and joyful space for activities, events, and community building.",
                        membershipData = listOf(
                            MembershipDataItem(
                                label = "Full name",
                                type = MembershipDataTypes.SHORT_TEXT,
                                required = true,
                            ),
                            MembershipDataItem(
                                label = "Emergency contact",
                                type = MembershipDataTypes.NUMBER,
                                required = false,
                            ),
                            MembershipDataItem(
                                label = "T-shirt size",
                                type = MembershipDataTypes.DROP_DOWN,
                                options = listOf("S", "M", "L"),
                                required = true,
                            ),
                        ),
                    ),
                    onBackClick = {},
                    onNameChanged = {},
                    onDescriptionChanged = {},
                    onUploadImageClick = {},
                    onAddFieldClick = {},
                    onEditFieldClick = {},
                    onDeleteFieldClick = {},
                    onSaveClick = {},
                    onEditorLabelChanged = {},
                    onEditorRequiredChanged = {},
                    onEditorTypeChanged = {},
                    onDismissEditor = {},
                    onSaveEditor = {},
                )
            }
        }
    }
}
